import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Linkedin,
  Link as LinkIcon,
  Check,
  MessageCircle,
  Send,
  MessageSquare,
  Flame,
  Share2,
} from "lucide-react";
import { BlueskyIcon } from "@/components/BlueskyIcon";
import { XIcon } from "@/components/XIcon";

interface Props {
  url: string;
  title: string;
}

export function ShareMenu({ url, title }: Props) {
  const [copied, setCopied] = useState(false);
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);

  const items: { label: string; href: string; Icon: React.FC<{ className?: string }> }[] = [
    { label: "X", href: `https://x.com/intent/tweet?url=${u}&text=${t}`, Icon: XIcon },
    {
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      Icon: Linkedin,
    },
    {
      label: "Bluesky",
      href: `https://bsky.app/intent/compose?text=${t}%20${u}`,
      Icon: BlueskyIcon,
    },
    {
      label: "Threads",
      href: `https://www.threads.net/intent/post?text=${t}%20${u}`,
      Icon: MessageCircle,
    },
    { label: "Telegram", href: `https://t.me/share/url?url=${u}&text=${t}`, Icon: Send },
    {
      label: "WhatsApp",
      href: `https://api.whatsapp.com/send?text=${t}%20${u}`,
      Icon: MessageSquare,
    },
    { label: "Reddit", href: `https://www.reddit.com/submit?url=${u}&title=${t}`, Icon: Flame },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Share this post"
          className="no-print inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 backdrop-blur transition-colors hover:bg-white/10 hover:text-white"
        >
          <Share2 className="h-3 w-3" />
          Share
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 border-white/15 bg-black/90 p-3 text-white backdrop-blur"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/75">
          Share this post
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {items.map(({ label, href, Icon }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Share on ${label}`}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white/75 transition-colors hover:border-white hover:text-white"
            >
              <Icon className="h-3.5 w-3.5" />
            </a>
          ))}
          <button
            onClick={copy}
            type="button"
            aria-label="Copy link"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white/75 transition-colors hover:border-white hover:text-white"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <LinkIcon className="h-3.5 w-3.5" />}
          </button>
        </div>
        {copied && (
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
            Link copied
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
